/**
 * Smooth slide open/close for PLP facet accordions (desktop sidebar + mobile drawer).
 */
(function () {
  const DESKTOP_DETAILS_SELECTOR = '.js-filter.facets__disclosure-vertical';
  const DESKTOP_SUMMARY_SELECTOR = `${DESKTOP_DETAILS_SELECTOR} > summary`;
  const MOBILE_DETAILS_SELECTOR = '.plp-mobile-facets .js-filter.mobile-facets__details';
  const MOBILE_SUMMARY_SELECTOR = `${MOBILE_DETAILS_SELECTOR} > summary`;
  const DESKTOP_PANEL_SELECTOR = '.facets__display-vertical, .facets__display';
  const MOBILE_PANEL_SELECTOR = '.mobile-facets__submenu';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ANIMATION_MS = 350;

  function isMobileViewport() {
    return window.matchMedia('(max-width: 980px)').matches;
  }

  function getPanel(details) {
    if (details.classList.contains('mobile-facets__details')) {
      return details.querySelector(MOBILE_PANEL_SELECTOR);
    }
    return details.querySelector(DESKTOP_PANEL_SELECTOR);
  }

  function clearInlinePanelStyles(panel) {
    panel.style.height = '';
    panel.style.opacity = '';
    panel.style.overflow = '';
  }

  function openPanel(details, panel, animate = true) {
    details.setAttribute('open', '');
    details.classList.add('facet-accordion--open');
    details.dataset.facetAccordionAnimating = 'true';

    if (!animate || REDUCED_MOTION) {
      clearInlinePanelStyles(panel);
      delete details.dataset.facetAccordionAnimating;
      return;
    }

    panel.style.overflow = 'hidden';
    panel.style.height = '0px';
    panel.style.opacity = '0';

    requestAnimationFrame(() => {
      panel.style.height = `${panel.scrollHeight}px`;
      panel.style.opacity = '1';
    });

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      panel.removeEventListener('transitionend', onTransitionEnd);
      window.clearTimeout(fallbackTimer);
      panel.style.height = 'auto';
      panel.style.overflow = '';
      delete details.dataset.facetAccordionAnimating;
    };

    const onTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') return;
      finish();
    };

    const fallbackTimer = window.setTimeout(finish, ANIMATION_MS + 50);
    panel.addEventListener('transitionend', onTransitionEnd);
  }

  function closePanel(details, panel, animate = true) {
    details.classList.remove('facet-accordion--open');
    details.dataset.facetAccordionAnimating = 'true';

    if (!animate || REDUCED_MOTION) {
      details.removeAttribute('open');
      clearInlinePanelStyles(panel);
      delete details.dataset.facetAccordionAnimating;
      return;
    }

    panel.style.overflow = 'hidden';
    panel.style.height = `${panel.scrollHeight}px`;
    panel.style.opacity = '1';

    requestAnimationFrame(() => {
      panel.style.height = '0px';
      panel.style.opacity = '0';
    });

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      panel.removeEventListener('transitionend', onTransitionEnd);
      window.clearTimeout(fallbackTimer);
      details.removeAttribute('open');
      clearInlinePanelStyles(panel);
      delete details.dataset.facetAccordionAnimating;
    };

    const onTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') return;
      finish();
    };

    const fallbackTimer = window.setTimeout(finish, ANIMATION_MS + 50);
    panel.addEventListener('transitionend', onTransitionEnd);
  }

  function prepareDetails(details) {
    const panel = getPanel(details);
    if (!panel) return;

    panel.classList.add('facet-accordion__panel');
    clearInlinePanelStyles(panel);

    if (details.open || details.classList.contains('facet-accordion--open')) {
      details.classList.add('facet-accordion--open');
      if (!details.hasAttribute('open')) details.setAttribute('open', '');
    } else {
      details.classList.remove('facet-accordion--open');
      panel.style.height = '0px';
      panel.style.opacity = '0';
      panel.style.overflow = 'hidden';
    }
  }

  /** Expand all mobile filter groups (e.g. after drawer close) without animation. */
  function openAllMobile(root = document, animate = false) {
    const scope = root && root.nodeType === 1 ? root : document;
    scope.querySelectorAll(MOBILE_DETAILS_SELECTOR).forEach((details) => {
      const panel = getPanel(details);
      if (!panel) return;

      panel.classList.add('facet-accordion__panel');
      details.classList.remove('menu-opening');
      delete details.dataset.facetAccordionAnimating;

      if (animate) {
        openPanel(details, panel, true);
      } else {
        details.setAttribute('open', '');
        details.classList.add('facet-accordion--open');
        clearInlinePanelStyles(panel);
      }
    });
  }

  function resetMobilePanels(root = document) {
    const scope = root && root.nodeType === 1 ? root : document;
    scope.querySelectorAll(`${MOBILE_DETAILS_SELECTOR} .facet-accordion__panel`).forEach((panel) => {
      panel.classList.remove('facet-accordion__panel');
      clearInlinePanelStyles(panel);
    });

    scope.querySelectorAll(MOBILE_DETAILS_SELECTOR).forEach((details) => {
      details.classList.remove('facet-accordion--open');
      delete details.dataset.facetAccordionAnimating;
    });
  }

  function handleSummaryClick(event) {
    const isMobile = isMobileViewport();
    const summarySelector = isMobile ? MOBILE_SUMMARY_SELECTOR : DESKTOP_SUMMARY_SELECTOR;
    const summary = event.target.closest(summarySelector);
    if (!summary) return;

    const details = summary.closest('details');
    if (!details || details.dataset.facetAccordionAnimating === 'true') return;

    const panel = getPanel(details);
    if (!panel) return;

    event.preventDefault();

    prepareDetails(details);

    if (details.classList.contains('facet-accordion--open')) {
      closePanel(details, panel);
    } else {
      openPanel(details, panel);
    }
  }

  function init(root = document) {
    const scope = root && root.nodeType === 1 ? root : document;

    if (isMobileViewport()) {
      scope.querySelectorAll(MOBILE_DETAILS_SELECTOR).forEach(prepareDetails);
      return;
    }

    resetMobilePanels(scope);
    scope.querySelectorAll(DESKTOP_DETAILS_SELECTOR).forEach(prepareDetails);
  }

  function reinit(root = document) {
    init(root);
  }

  window.FacetAccordion = { init, reinit, openAllMobile, resetMobilePanels };

  document.addEventListener('click', handleSummaryClick, true);
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.addEventListener('resize', () => {
    if (isMobileViewport()) {
      init();
    } else {
      resetMobilePanels();
      init();
    }
  });
})();
